/** A contact currently in the workspace, as returned by `listContacts`. */
export type ContactSummary = {
  contactId: string;
  type?: string;
  subtype?: string;
  initialContactId?: string;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

/**
 * Best-effort contact id from an SDK payload. Lifecycle events carry `contactId` at the top
 * level; participant add/disconnect (supervisor monitor/barge) nest it under `participant`;
 * the app handshake may put it on `scope`.
 */
export function extractContactId(payload: unknown): string | undefined {
  if (!isRecord(payload)) return undefined;
  return (
    str(payload.contactId) ??
    (isRecord(payload.participant) ? str(payload.participant.contactId) : undefined) ??
    (isRecord(payload.contact) ? str(payload.contact.contactId) : undefined) ??
    (isRecord(payload.scope) ? str(payload.scope.contactId) : undefined)
  );
}

/**
 * Turns a `listContacts` result into roster rows. The SDK type is `CoreContactData[]`.
 * MockGateway's `{ __mock: true }` stand-in is not a contact list and yields nothing.
 */
export function extractContactSummaries(payload: unknown): ContactSummary[] {
  const rows = contactRows(payload);
  const out: ContactSummary[] = [];
  for (const row of rows) {
    const contactId = extractContactId(row);
    if (!contactId || !isRecord(row)) continue;
    out.push({
      contactId,
      type: str(row.type),
      subtype: str(row.subtype),
      initialContactId: str(row.initialContactId),
    });
  }
  return out;
}

/** Accepts a raw array or a `{ contacts: [...] }` wrapper; ignores mock stand-ins. */
function contactRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (isRecord(payload) && Array.isArray(payload.contacts)) return payload.contacts;
  return [];
}
